'use client';

import { c as _c } from "react/compiler-runtime";
import { usePaxLabel } from '@7f-ui/components/booking-box/booking-box';
import { Button } from '@7f-ui/components/button/button';
import { LoadingButton } from '@7f-ui/components/button/loading-button';
import { Price } from '@7f-ui/components/price';
import { useDateLabel } from '@7f-ui/hooks/use-date-label';
import type { AccommodationRate } from '@data-access/graphql/schema.types';
import type { AvailableRoom } from '@feat/feat-accommodation/availability';
import type { AccommodationMutationInput } from '@feat/feat-accommodation/booking';
import { RatesListContainer, useAccommodationMutation } from '@feat/feat-accommodation/booking';
import { AccommodationRoomDetailsModal, AccommodationRoomFacilitiesFlags } from '@feat/feat-accommodation/details';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useSelectedRoomRate } from '@feat/feat-room-picker/store';
import { trackCtaSelection } from '@feat/feat-tracking/cta';
import { useUrlState } from '@feat/feat-url-state/use-url-state';
import { shopAsset } from '@utils/utils-config';
import { shopTestId } from '@utils/utils-shop-test-ids';
import Image from 'next/image';
import { useState } from 'react';
import { HOTEL_ADS_NAV_ORDER } from 'src/lib/hotel-ads/nav';
import { useHotelAdsCriticalPath } from 'src/lib/hotel-ads/use-hotel-ads-critical-path';
import { AppAccommodationRoomImageSlider } from './app-accommodation-room-image-slider';
export type AppAccommodationAvailableRoomResult = {
  rate: AccommodationRate | null;
  room: AvailableRoom | null;
};
type AppAccommodationAvailableRoomProps = {
  room: AvailableRoom;
  isCheapestRoom: boolean;
  accommodation: AccommodationMutationInput;
  onSubmit?: (result: AppAccommodationAvailableRoomResult) => Promise<void>;
  onSelectRate?: (rate: AccommodationRate, room: AvailableRoom) => void;
  selectedRate?: string;
  showRoomCount?: boolean;
};
export function AppAccommodationAvailableRoom(t0) {
  const $ = _c(43);
  const {
    room,
    isCheapestRoom,
    accommodation,
    onSubmit,
    onSelectRate,
    selectedRate,
    showRoomCount: t1
  } = t0;
  const showRoomCount = t1 === undefined ? true : t1;
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  let t2;
  if ($[0] !== room.title) {
    t2 = () => {
      setIsDetailsModalOpen(true);
      trackCtaSelection("show room details", {
        type: "accommodation",
        selection: room.title
      });
    };
    $[0] = room.title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleShowDetailsClick = t2;
  let t3;
  if ($[2] !== room.images.length || $[3] !== room.title) {
    t3 = room.images.length === 0 && <div className="relative aspect-3/2 w-full overflow-hidden rounded-xl sm:aspect-4/3"><Image className="object-cover" src={shopAsset("/images/7f-logo-fallback.svg")} alt={room.title} width={640} height={640} /></div>;
    $[2] = room.images.length;
    $[3] = room.title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== room.images) {
    t4 = room.images.length !== 0 && <AppAccommodationRoomImageSlider className="aspect-4/3" images={room.images} />;
    $[5] = room.images;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="sm:col-span-5"><div className="relative overflow-hidden rounded-sm">{t3}{t4}</div></div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== room.title) {
    t6 = <h2 className="font-heading2">{room.title}</h2>;
    $[10] = room.title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== room.facilityHighlights) {
    t7 = room.facilityHighlights.slice(0, 6);
    $[12] = room.facilityHighlights;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = shopTestId("AccommodationAvailableRoom.showDetails");
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== handleShowDetailsClick) {
    t9 = <Button variant="underline" onClick={handleShowDetailsClick} data-testid={t8}>Zimmerdetails anzeigen</Button>;
    $[15] = handleShowDetailsClick;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t9) {
    t10 = <AccommodationRoomFacilitiesFlags facilities={t7}>{t9}</AccommodationRoomFacilitiesFlags>;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== isCheapestRoom || $[21] !== onSelectRate || $[22] !== room || $[23] !== selectedRate) {
    t11 = <RatesListContainer room={room} onSelectRate={onSelectRate} selectedRate={selectedRate} isCheapestRoom={isCheapestRoom} />;
    $[20] = isCheapestRoom;
    $[21] = onSelectRate;
    $[22] = room;
    $[23] = selectedRate;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== accommodation || $[26] !== onSubmit || $[27] !== room.id || $[28] !== showRoomCount) {
    t12 = <AddToCart accommodation={accommodation} roomId={room.id} onSubmit={onSubmit} showRoomCount={showRoomCount} />;
    $[25] = accommodation;
    $[26] = onSubmit;
    $[27] = room.id;
    $[28] = showRoomCount;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t6) {
    t13 = <div className="bleed-container full-bleed place-content-start gap-y-6 sm:col-span-7 sm:grid-cols-1 sm:*:col-start-1">{t6}{t10}{t11}{t12}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t6;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setIsDetailsModalOpen(false);
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== isDetailsModalOpen || $[37] !== room) {
    t15 = <AccommodationRoomDetailsModal room={room} isOpen={isDetailsModalOpen} onClose={t14} />;
    $[36] = isDetailsModalOpen;
    $[37] = room;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t13 || $[40] !== t15 || $[41] !== t5) {
    t16 = <>{t5}{t13}{t15}</>;
    $[39] = t13;
    $[40] = t15;
    $[41] = t5;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
type AddToCartProps = {
  accommodation: AccommodationMutationInput;
  roomId: string;
  onSubmit?: (result: AppAccommodationAvailableRoomResult) => Promise<void>;
  showRoomCount?: boolean;
};
function AddToCart(t0) {
  const $ = _c(30);
  const {
    accommodation,
    roomId,
    onSubmit,
    showRoomCount: t1
  } = t0;
  const showRoomCount = t1 === undefined ? true : t1;
  const {
    urlState
  } = useUrlState();
  const t2 = urlState.children?.length;
  let t3;
  if ($[0] !== showRoomCount || $[1] !== t2 || $[2] !== urlState.adults) {
    t3 = {
      adults: urlState.adults,
      children: t2,
      showRooms: showRoomCount
    };
    $[0] = showRoomCount;
    $[1] = t2;
    $[2] = urlState.adults;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const paxLabel = usePaxLabel(t3);
  let t4;
  if ($[4] !== urlState.from || $[5] !== urlState.to) {
    t4 = {
      fromDate: urlState.from,
      toDate: urlState.to
    };
    $[4] = urlState.from;
    $[5] = urlState.to;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const dateLabel = useDateLabel(t4);
  const {
    rate,
    room
  } = useSelectedRoomRate();
  const {
    addAccommodation
  } = useAccommodationMutation(accommodation.id);
  const isCurrencyMismatch = useIsCartCurrencyMismatch(onSubmit ? undefined : rate?.pricing.currency);
  const navAttributes = useHotelAdsCriticalPath(HOTEL_ADS_NAV_ORDER.addToCart, "desktop");
  if (!rate || room?.id !== roomId) {
    return null;
  }
  let t5;
  if ($[7] !== accommodation || $[8] !== addAccommodation || $[9] !== onSubmit || $[10] !== rate || $[11] !== room) {
    t5 = async () => {
      if (onSubmit) {
        await onSubmit({
          rate,
          room
        });
        return;
      }
      await addAccommodation({
        accommodation,
        rateId: rate.id,
        room
      });
    };
    $[7] = accommodation;
    $[8] = addAccommodation;
    $[9] = onSubmit;
    $[10] = rate;
    $[11] = room;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleAddToCart = t5;
  let t6;
  if ($[13] !== rate.pricing.currency || $[14] !== rate.pricing.price) {
    t6 = <span><Price className="font-body-copy font-semibold" amount={rate.pricing.price} currency={rate.pricing.currency} />{" inkl. MwSt."}</span>;
    $[13] = rate.pricing.currency;
    $[14] = rate.pricing.price;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== dateLabel || $[17] !== paxLabel) {
    t7 = <span>{dateLabel} {" / "} {paxLabel}</span>;
    $[16] = dateLabel;
    $[17] = paxLabel;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <div className="flex flex-row flex-wrap items-baseline gap-x-2 font-body-small">{t6}{t7}</div>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = shopTestId("AccommodationAvailableRoom.addToCart");
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== handleAddToCart || $[24] !== isCurrencyMismatch || $[25] !== navAttributes) {
    t10 = <LoadingButton size="medium" disabled={isCurrencyMismatch} onClick={handleAddToCart} data-testid={t9} {...navAttributes}>In den Warenkorb</LoadingButton>;
    $[23] = handleAddToCart;
    $[24] = isCurrencyMismatch;
    $[25] = navAttributes;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t8) {
    t11 = <div className="full-bleed hidden items-center justify-between gap-x-2 sm:flex">{t8}{t10}</div>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}